import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ClickToCopy,
  Dialog,
  DialogContent,
  DialogTrigger,
  Form,
  SubmitButton,
} from "@bleu.builders/ui";
import { useMachine } from "@xstate/react";
import { UsersIcon } from "lucide-react";
import React from "react";
import { useForm } from "react-hook-form";

import { Link } from "#/components/Link";
import { useOrganization } from "#/contexts/OrganizationContext";
import { diagnosesCreationStateMachine } from "#/lib/states/multi_create_external_diagnosis";

import { CreationError } from "./shared";
import { SuccessIcon } from "./SuccessIcon";

function CreationInProgress({
  title,
  description,
  path,
  form,
  state,
  handleSubmit,
}: {
  title: string;
  description: string;
  path: string;
  form: ReturnType<typeof useForm>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  state: any;
  handleSubmit: () => () => void;
}) {
  return (
    <Card className="w-full max-w-none bg-background border-0 shadow-none px-0">
      <CardHeader className="justify-center items-center">
        <UsersIcon className="size-16 border text-muted-foreground p-2 rounded-sm" />
        <CardTitle className="text-xl">{title}</CardTitle>
        <CardDescription className="py-0 text-sm">
          {description}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form action={path} method="post" className="flex gap-2" {...form}>
          <Link to="/settings/brand_theme" className="w-full">
            <Button type="button" variant="outline" className="w-full">
              Personalizar
            </Button>
          </Link>
          <SubmitButton
            type="button"
            onClick={handleSubmit()}
            submittingText="Gerando link..."
            isSubmitting={state.value === "creationInProgress"}
            className="w-full"
          >
            Gerar link
          </SubmitButton>
        </Form>
      </CardContent>
    </Card>
  );
}

function SuccessCreation({
  state,
}: {
  title?: string;
  resourceTitle?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  state: any;
}) {
  return (
    <Card className="w-full max-w-none bg-background border-0 shadow-none px-0">
      <CardHeader className="justify-center items-center">
        <SuccessIcon />
        <CardTitle className="text-xl">
          Seu link compartilhável foi criado!
        </CardTitle>
        <CardDescription className="py-0 text-sm">
          Cada formulário respondido irá gerar um diagnóstico tributário
          individualizado para aquele cliente e você será avisado a cada
          resposta.
        </CardDescription>
      </CardHeader>
      <CardContent className="w-full flex flex-col gap-4">
        <div className="flex gap-2 justify-center items-center">
          <Link to="/settings/brand_theme" className="w-fit">
            <Button type="button" variant="outline" className="w-fit">
              Personalizar
            </Button>
          </Link>

          <ClickToCopy
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2"
            text={state.context.externalUrl}
          >
            Copiar link
          </ClickToCopy>
        </div>
        <div className="text-xs text-muted-foreground">
          <span>Ou compartilhe o link abaixo: </span>
          <span className="text-primary">{state.context.externalUrl}</span>
        </div>
      </CardContent>
    </Card>
  );
}

export function CreateMultipleDiagnosisLinkForm({
  title,
  description,
  path,
}: {
  title: string;
  description: string;
  path: string;
}) {
  const form = useForm();
  const [state, send] = useMachine(diagnosesCreationStateMachine);
  const organization = useOrganization();

  const handleSubmit = () => () => {
    send({
      type: "SUBMIT_LINK_DATA",
      organizationId: organization?.id,
    });
  };

  switch (state.value) {
    case "confirmation":
    case "creationInProgress":
      return (
        <CreationInProgress
          title={title}
          description={description}
          path={path}
          form={form}
          state={state}
          handleSubmit={handleSubmit}
        />
      );
    case "linkCreated":
      return <SuccessCreation state={state} />;
    case "linkCreationError":
      return (
        <CreationError
          title="Erro ao criar diagnóstico externo"
          description="Ocorreu um erro ao criar diagnóstico externo."
          creationError={state.context.creationError}
        />
      );
    default:
      return null;
  }
}

export const MultipleDiagnosisLinkCreation = () => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full justify-start">
          <UsersIcon className="mr-2 size-4" />
          Gerar link para múltiplos diagnósticos
          <Badge className="ml-2 bg-orange-500 hover:bg-orange-400">Novo</Badge>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[550px]">
        <CreateMultipleDiagnosisLinkForm
          title="Link compartilhável para vários clientes"
          description="Acelere seu processo comercial e encaminhe um único link para realizar o diagnóstico tributário de quantos clientes quiser."
          path={"/"}
        />
      </DialogContent>
    </Dialog>
  );
};
